import { Box, SimpleGrid, Text, VStack } from "@chakra-ui/react";
import { asHTML, asText } from "@prismicio/client";
import { checklistSx } from "./richTextStyles";

// Styling matches the website's DualColumnContent slice exactly (antaia-website
// src/slices/DualColumnContent, "Light / Reversed" variant) — light-tint lozenges,
// a two-line title (first line dark ink, second line green — split at the first
// period), and a green-checkmark list rather than default browser bullets.
const GREEN = "#5A8A1A";
const HEADING_INK = "#0E3137";

function Lozenge({ text }) {
  return (
    <Text display="inline-block" px={3} py={1.5} fontSize="12px" fontWeight="500" bg="lightLime" color={HEADING_INK} borderRadius="full">
      {text}
    </Text>
  );
}

// Splits rich-text heading1 nodes at the first ". " so the first sentence renders
// in dark ink and the rest in green — matches the website's splitAtPeriod() (see
// antaia-website src/slices/DualColumnContent/index.tsx).
function splitTitleAtPeriod(richTextField) {
  const text = asText(richTextField);
  if (!text) return { first: "", rest: "" };
  const idx = text.indexOf(". ");
  if (idx === -1) return { first: text, rest: "" };
  return { first: text.slice(0, idx + 1), rest: text.slice(idx + 1).trimStart() };
}

// Left description's rich text renders through Chakra `sx` (paragraph + the
// shared green-checkmark list, see richTextStyles.js checklistSx).
const DESCRIPTION_SX = {
  p: { fontSize: "body.l", color: "gray.600", mb: 4, lineHeight: "1.7" },
  strong: { fontWeight: "700", color: HEADING_INK },
  em: { color: GREEN, fontStyle: "normal" },
  ...checklistSx,
  li: { ...checklistSx.li, fontSize: "body.l" },
};

const RIGHT_TITLE_SX = {
  h2: { fontSize: { base: "22px", md: "28px" }, fontWeight: "700", color: GREEN, lineHeight: "1.3", mb: 4, mt: 0 },
  strong: { color: GREEN },
  em: { color: GREEN, fontStyle: "normal" },
};

export default function DualColumnContentSlice({ slice }) {
  const { left_title, left_column_lozenge, left_description, right_badge, right_title, right_description, right_description_first } =
    slice.primary;

  const rightBadgeText = right_badge?.length > 0 ? asText(right_badge) : null;
  const { first: titleFirst, rest: titleRest } = splitTitleAtPeriod(left_title);

  return (
    <VStack align="stretch" gap={8}>
      <VStack align="start" gap={6}>
        {left_column_lozenge && <Lozenge text={left_column_lozenge} />}
        {(titleFirst || titleRest) && (
          <VStack align="start" gap={0}>
            {titleFirst && (
              <Text fontSize={{ base: "32px", md: "44px" }} fontWeight="800" color={HEADING_INK} lineHeight="1.15">
                {titleFirst}
              </Text>
            )}
            {titleRest && (
              <Text fontSize={{ base: "32px", md: "44px" }} fontWeight="800" color={GREEN} lineHeight="1.15">
                {titleRest}
              </Text>
            )}
          </VStack>
        )}
      </VStack>

      <SimpleGrid columns={{ base: 1, md: 2 }} gap={{ base: 8, md: 12 }}>
        <Box>
          {left_description?.length > 0 && (
            <Box sx={DESCRIPTION_SX} dangerouslySetInnerHTML={{ __html: asHTML(left_description) }} />
          )}
        </Box>

        <Box>
          {rightBadgeText && (
            <Box mb={4}>
              <Lozenge text={rightBadgeText} />
            </Box>
          )}
          {right_description_first && right_description?.length > 0 && (
            <Box sx={DESCRIPTION_SX} dangerouslySetInnerHTML={{ __html: asHTML(right_description) }} />
          )}
          {right_title?.length > 0 && <Box sx={RIGHT_TITLE_SX} dangerouslySetInnerHTML={{ __html: asHTML(right_title) }} />}
          {!right_description_first && right_description?.length > 0 && (
            <Box sx={DESCRIPTION_SX} dangerouslySetInnerHTML={{ __html: asHTML(right_description) }} />
          )}
        </Box>
      </SimpleGrid>
    </VStack>
  );
}
